import React, { createContext, useContext, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { StoreAuthContextType, StorePermission } from '@/types/shop';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { useStorePermissions } from '@/hooks/useStorePermissions';
import { clearUserTypeCache } from '@/hooks/useUserType';
import { clearSessionContext } from '@/services/sessionStorage';
import { useSessionContext } from '@/context/SessionContextProvider';
import { clearEnvironmentChoice } from '@/lib/environmentChoice';

const StoreAuthContext = createContext<StoreAuthContextType | undefined>(undefined);

export const StoreAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session, status: authStatus, isLoading: isAuthLoading, signIn, signOut: supabaseSignOut, retry } = useSupabaseAuth();
  const queryClient = useQueryClient();
  
  // Obter storeId diretamente do React state (SessionContext) — sem polling de storage
  const { storeId } = useSessionContext();
  
  // Buscar permissões store-scoped usando a nova RPC
  const { 
    permissions: storePermissions, 
    isLoading: isPermissionsLoading,
    isError: isPermissionsError,
    hasPermission: hasStorePermission,
    hasAnyPermission: hasAnyStorePermission,
  } = useStorePermissions({ 
    storeId, 
    enabled: !!user?.id && !!storeId && authStatus === 'authenticated'
  });

  const login = async (email: string, password: string, captchaToken?: string): Promise<void> => {
    await signIn(email, password, captchaToken);
  };

  const logout = async () => {
    await supabaseSignOut();
    clearEnvironmentChoice();
    
    // Limpar todos os caches para evitar dados do usuário anterior
    queryClient.clear();
    clearUserTypeCache();
    clearSessionContext();
  };

  /**
   * Verifica se o usuário tem uma permissão específica
   * Usa permissões store-scoped quando há loja selecionada,
   * caso contrário usa permissões legadas do perfil
   */
  const hasPermission = (permission: StorePermission): boolean => {
    if (!user) {
      return false;
    }

    // Masters têm todas as permissões
    if (user.isMaster) {
      return true;
    }

    // Se tem loja selecionada E permissões carregadas, usar store-scoped
    if (storeId && storePermissions.length > 0) {
      return hasStorePermission(permission);
    }

    // Se tem loja mas ainda está carregando permissões, retornar false temporariamente
    if (storeId && isPermissionsLoading) {
      return false;
    }

    // Fallback: usar permissões do perfil (legado) - útil para modo NETWORK ou sem loja
    const permissionsToCheck = user.isCustomPermissions && user.customPermissions
      ? user.customPermissions
      : user.permissions;

    return permissionsToCheck.includes(permission);
  };

  /**
   * Verifica se o usuário tem qualquer uma das permissões
   */
  const hasAnyPermission = (permissions: StorePermission[]): boolean => {
    if (!user) return false;
    if (user.isMaster) return true;
    
    // Se tem loja selecionada e permissões carregadas, usar store-scoped
    if (storeId && storePermissions.length > 0) {
      return hasAnyStorePermission(permissions);
    }

    // Fallback legado
    return permissions.some(p => hasPermission(p));
  };

  // Calcular loading combinado
  const isLoading = authStatus === 'booting' || (!!user?.id && !!storeId && isPermissionsLoading);
  const status = isPermissionsError ? 'error' : authStatus;

  // Memoizar valor do contexto para evitar re-renders desnecessários
  const contextValue = useMemo(() => ({
    user,
    store: null,
    isLoading,
    status,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    retry,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, isLoading, status, storeId, storePermissions, isPermissionsLoading]);

  return (
    <StoreAuthContext.Provider value={contextValue}>
      {children}
    </StoreAuthContext.Provider>
  );
};

export const useStoreAuth = () => {
  const context = useContext(StoreAuthContext);
  if (!context) {
    throw new Error('useStoreAuth must be used within a StoreAuthProvider');
  }
  return context;
};

// Mock allUsers para compatibilidade com código existente
// TODO: Remover quando todos os componentes migrarem para buscar do Supabase
export const allUsers = [];
